import { isEmpty, uniq } from 'lodash'
import { isKeyOf } from 'ComponentLibrary/src/common/type-predicates'
import { ExhaustivenessError } from 'ComponentLibrary/src/common/error/error'

export const STRUCTURE_USAGE_TYPES = ['residential', 'office', 'retail', 'mixed', 'garage', 'other'] as const

export type StructureUsageType = (typeof STRUCTURE_USAGE_TYPES)[number]

export const STRUCTURE_SUB_USAGE_TYPES_BY_USAGE_TYPE = {
    residential: [
        'single_family_house',
        'two_family_house',
        'semi_detached_house',
        'townhouse_middle',
        'townhouse_end',
        'apartment',
        'apartment_building',
        'mixed_use_apartment_building',
    ],
    office: ['office'],
    retail: ['consumer_market', 'department_store', 'car_dealer'],
    mixed: ['commercial_structure'],
    garage: [
        'single_car_garage',
        'multi_car_garage',
        'multi_story_parking_garage',
        'underground_parking_garage',
        'commercial_vehicle_garage',
    ],
    other: ['other'],
} as const satisfies Record<StructureUsageType, readonly string[]>

export type StructureSubUsageType<T extends StructureUsageType = StructureUsageType> =
    (typeof STRUCTURE_SUB_USAGE_TYPES_BY_USAGE_TYPE)[T][number]

export const DEFAULT_SUB_USAGE_TYPE = {
    residential: 'single_family_house',
    office: 'office',
    retail: 'consumer_market',
    mixed: 'commercial_structure',
    garage: 'single_car_garage',
    other: 'other',
} as const satisfies { [T in StructureUsageType]: StructureSubUsageType<T> }

export type AssetUsageType = {
    usage_type: StructureUsageType | 'unknown'
    sub_usage_type?: string
}

export const USAGE_TYPE_LABELS = {
    residential: 'Wohnen',
    office: 'Büro',
    retail: 'Handel',
    mixed: 'Gemischt genutzte Immobilie',
    garage: 'Parken',
    other: 'Sonstige',
}

type UsageType = keyof typeof USAGE_TYPE_LABELS

export const getStructureUsageTypesText = (usageTypes: UsageType[]) => {
    const usageTypesMapped = uniq(usageTypes.map((usageType) => USAGE_TYPE_LABELS[usageType]))
    return usageTypesMapped.join(' | ')
}

export const RESIDENTIAL_SUB_USAGE_TYPE_LABELS = {
    single_family_house: 'Einfamilienhaus',
    apartment: 'Eigentumswohnung',
    two_family_house: 'Zweifamilienhaus',
    semi_detached_house: 'Doppelhaushälfte',
    townhouse_middle: 'Reihenmittelhaus',
    townhouse_end: 'Reihenendhaus',
    apartment_building: 'Mehrfamilienhaus',
    office: 'Büro',
    retail: 'Handel',
    mixed: 'Wohnen und Gewerbe',
    mixed_use_apartment_building: 'Wohnhaus mit Mischnutzung',
}

export const GARAGE_SUB_USAGE_TYPE_LABELS = {
    single_car_garage: 'Einzelgarage',
    multi_car_garage: 'Mehrfachgarage',
    multi_story_parking_garage: 'Hochgarage',
    underground_parking_garage: 'Tiefgarage',
    commercial_vehicle_garage: 'Nutzfahrzeuggarage',
}

export const MIXED_SUB_USAGE_TYPE_LABELS = {
    commercial_structure: 'Wohn- und Geschäftshaus',
}

export const OFFICE_SUB_USAGE_TYPE_LABELS = {
    office_structure: 'Büro',
}

export const OTHER_SUB_USAGE_TYPE_LABELS = {
    other: 'Sonstige',
}

export const RETAIL_SUB_USAGE_TYPE_LABELS = {
    consumer_market: 'Verbrauchermarkt',
    department_store: 'Kauf-/Warenhaus',
    car_dealer: 'Autohaus',
}

export const STRUCTURE_SUB_USAGE_TYPE_LABELS = {
    single_family_house: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.single_family_house,
    two_family_house: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.two_family_house,
    semi_detached_house: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.semi_detached_house,
    townhouse_middle: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.townhouse_middle,
    townhouse_end: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.townhouse_end,
    apartment: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.apartment,
    apartment_building: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.apartment_building,
    mixed_use_apartment_building: RESIDENTIAL_SUB_USAGE_TYPE_LABELS.mixed_use_apartment_building,
    office: USAGE_TYPE_LABELS.office,
    consumer_market: RETAIL_SUB_USAGE_TYPE_LABELS.consumer_market,
    department_store: RETAIL_SUB_USAGE_TYPE_LABELS.department_store,
    car_dealer: RETAIL_SUB_USAGE_TYPE_LABELS.car_dealer,
    commercial_structure: MIXED_SUB_USAGE_TYPE_LABELS.commercial_structure,
    single_car_garage: GARAGE_SUB_USAGE_TYPE_LABELS.single_car_garage,
    multi_car_garage: GARAGE_SUB_USAGE_TYPE_LABELS.multi_car_garage,
    multi_story_parking_garage: GARAGE_SUB_USAGE_TYPE_LABELS.multi_story_parking_garage,
    underground_parking_garage: GARAGE_SUB_USAGE_TYPE_LABELS.underground_parking_garage,
    commercial_vehicle_garage: GARAGE_SUB_USAGE_TYPE_LABELS.commercial_vehicle_garage,
    other: OTHER_SUB_USAGE_TYPE_LABELS.other,
} satisfies Record<StructureSubUsageType, string>

const getSubUsageTypeLabel = (subUsageTypeMap: Record<string, string>, subUsageType?: string): string | undefined => {
    if (subUsageType && isKeyOf(subUsageType, subUsageTypeMap)) {
        return subUsageTypeMap[subUsageType]
    }
    return undefined
}

export const getAssetUsageTypesText = (assetUsageTypes?: AssetUsageType[]): string[] => {
    if (!assetUsageTypes || isEmpty(assetUsageTypes)) {
        return []
    }

    return assetUsageTypes.map(({ usage_type, sub_usage_type }) => {
        switch (usage_type) {
            case 'residential':
                return getSubUsageTypeLabel(RESIDENTIAL_SUB_USAGE_TYPE_LABELS, sub_usage_type) || USAGE_TYPE_LABELS['residential']
            case 'retail':
                return getSubUsageTypeLabel(RETAIL_SUB_USAGE_TYPE_LABELS, sub_usage_type) || USAGE_TYPE_LABELS['retail']
            case 'office':
                return getSubUsageTypeLabel(OFFICE_SUB_USAGE_TYPE_LABELS, sub_usage_type) || USAGE_TYPE_LABELS['office']
            case 'mixed':
                return getSubUsageTypeLabel(MIXED_SUB_USAGE_TYPE_LABELS, sub_usage_type) || USAGE_TYPE_LABELS['mixed']
            case 'garage':
                return getSubUsageTypeLabel(GARAGE_SUB_USAGE_TYPE_LABELS, sub_usage_type) || USAGE_TYPE_LABELS['garage']
            case 'other':
                return getSubUsageTypeLabel(OTHER_SUB_USAGE_TYPE_LABELS, sub_usage_type) || sub_usage_type || 'Sonstige'
            case 'unknown':
                return 'Unbekannt'
            default:
                throw ExhaustivenessError(usage_type)
        }
    })
}
